import { useEffect } from "react";

// Keep in sync with packages/web/index.html — the defaults this hook
// restores on unmount so SPA navigation away from an entity page (peak,
// collection, …) back to a page that doesn't call usePageMeta (or to a page
// that hasn't mounted its own values yet) doesn't leave stale tags behind.
const SITE_URL = "https://goatlas.co.uk";
const DEFAULT_TITLE = "Atlas - the record of your time outside";
const DEFAULT_DESCRIPTION =
  "Atlas turns the walks you have already recorded into collections, achievements and coverage maps. The record of your time outside, with a running list of what is left.";
const DEFAULT_CANONICAL = `${SITE_URL}/`;
const DEFAULT_OG_IMAGE = `${SITE_URL}/og-image.png`;

export interface PageMetaOptions {
  title: string;
  description: string;
  /** Path only, e.g. "/peaks/scafell-pike" — absolutised against SITE_URL. */
  canonicalPath: string;
  /** Absolute URL or site-relative path; defaults to the site og-image. */
  ogImage?: string;
  /**
   * Set false while the data behind the tags is still loading, so
   * placeholder values ("<slug> — 0 peaks checklist") never reach the
   * head. Until enabled, the prerendered/static tags stay in place, which
   * is exactly what crawlers should see.
   */
  enabled?: boolean;
  /**
   * Adds `<meta name="robots" content="noindex">` for the page's lifetime
   * (removed on unmount). For public pages that should never be indexed,
   * such as invite landings.
   */
  noindex?: boolean;
}

function setRobots(noindex: boolean) {
  const existing = document.head.querySelector<HTMLMetaElement>('meta[name="robots"]');
  if (noindex) {
    if (existing) existing.setAttribute("content", "noindex");
    else setMeta("name", "robots", "noindex");
  } else if (existing) {
    existing.remove();
  }
}

function setMeta(attr: "name" | "property", key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setLink(rel: string, href: string) {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function applyAll(title: string, description: string, canonical: string, image: string) {
  document.title = title;
  setMeta("name", "description", description);
  setLink("canonical", canonical);
  setMeta("property", "og:title", title);
  setMeta("property", "og:description", description);
  setMeta("property", "og:url", canonical);
  setMeta("property", "og:image", image);
  setMeta("name", "twitter:title", title);
  setMeta("name", "twitter:description", description);
  setMeta("name", "twitter:image", image);
}

/**
 * Sets document title, meta description, canonical link and OG/Twitter tags
 * for the lifetime of the calling component; restores the site defaults
 * (from index.html) on unmount. Used on public entity/detail pages for
 * programmatic SEO — this is a client-side-only SPA, so these tags only
 * matter to crawlers that execute JS, but they also drive social-share
 * previews for links copied out of the app.
 */
export function usePageMeta({ title, description, canonicalPath, ogImage, enabled = true, noindex = false }: PageMetaOptions): void {
  useEffect(() => {
    if (!enabled) return;
    const canonical = `${SITE_URL}${canonicalPath}`;
    const image = ogImage
      ? ogImage.startsWith("http")
        ? ogImage
        : `${SITE_URL}${ogImage}`
      : DEFAULT_OG_IMAGE;

    applyAll(title, description, canonical, image);
    if (noindex) setRobots(true);

    return () => {
      applyAll(DEFAULT_TITLE, DEFAULT_DESCRIPTION, DEFAULT_CANONICAL, DEFAULT_OG_IMAGE);
      if (noindex) setRobots(false);
    };
  }, [title, description, canonicalPath, ogImage, enabled, noindex]);
}
